import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate } from 'react-router-dom'
import { api } from '../../services/api'
import { getMe } from '../../store/authSlice'
import { AppDispatch, RootState } from '../../store/store'

const OBJECTIVES = [
  { id: 'gain_muscle', label: 'Ganancia de masa muscular' },
  { id: 'lose_fat', label: 'Pérdida de grasa' },
  { id: 'recomp', label: 'Recomposición corporal (perder grasa mientras ganas masa muscular)' },
  { id: 'maintain', label: 'Mantenimiento' },
  { id: 'ideal_weight', label: 'Peso ideal' },
  { id: 'cardio', label: 'Resistencia cardiovascular' },
]

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo']
const MEAL_OPTIONS = [2, 3, 5, 6]

function imc(weight: string, height: string) {
  const w = Number(weight)
  const h = Number(height)
  if (!w || !h) return ''
  return (w / ((h / 100) ** 2)).toFixed(1)
}

export default function WelcomeIntakePage() {
  const dispatch = useDispatch<AppDispatch>()
  const { user } = useSelector((s: RootState) => s.auth)
  const [step, setStep] = useState(0)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState<any>({
    email: user?.email || '',
    fullName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
    phone: user?.phone || '',
    age: '',
    birthDate: '',
    city: '',
    gender: '',
    profession: '',
    objective: '',
    objectiveDetail: '',
    heightCm: '',
    weightKg: '',
    bodyFatPct: '',
    daysAvailable: '',
    trainDays: [] as string[],
    gymName: '',
    trainMinutes: '',
    trainingExperience: '',
    trainingPreference: '',
    injuries: '',
    diseases: '',
    specialPathology: '',
    dizziness: '',
    heartMeds: '',
    otherLimits: '',
    sport: '',
    smokeAlcohol: '',
    sleepHours: '',
    workSchedule: '',
    mealsNow: '',
    mealsWanted: '',
    eatOut: '',
    unwantedFoods: '',
    waterLiters: '',
    supplements: '',
    allergies: '',
    wantedFoods: '',
    socialMeals: '',
  })

  useEffect(() => {
    api.get('/nutrition/intake').then(({ data }) => {
      const pre = data?.prefill || {}
      setForm((prev: any) => ({
        ...prev,
        email: pre.email || prev.email,
        fullName: pre.fullName || prev.fullName,
        phone: pre.phone || prev.phone,
        birthDate: pre.birthDate ? String(pre.birthDate).slice(0, 10) : prev.birthDate,
        gender: pre.gender || prev.gender,
        weightKg: pre.weightKg || prev.weightKg,
        heightCm: pre.heightCm || prev.heightCm,
      }))
    }).catch(() => {})
  }, [])

  const bmi = useMemo(() => imc(form.weightKg, form.heightCm), [form.weightKg, form.heightCm])

  const set = (key: string, value: any) => setForm((prev: any) => ({ ...prev, [key]: value }))

  const toggleDay = (day: string) => {
    setForm((prev: any) => {
      const has = prev.trainDays.includes(day)
      return { ...prev, trainDays: has ? prev.trainDays.filter((d: string) => d !== day) : [...prev.trainDays, day] }
    })
  }

  const submit = async () => {
    setError('')
    if (!form.objective) { setError('Selecciona un objetivo'); setStep(1); return }
    if (![2, 3, 5, 6].includes(Number(form.mealsWanted))) { setError('Indica cuántas comidas quieres al día'); setStep(3); return }
    if (!form.weightKg || !form.heightCm) { setError('Estatura y peso son obligatorios'); setStep(0); return }
    setSaving(true)
    try {
      await api.post('/nutrition/intake', {
        ...form,
        mealsWanted: Number(form.mealsWanted),
        weightKg: Number(form.weightKg),
        heightCm: Number(form.heightCm),
      })
      await dispatch(getMe())
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo guardar el formulario')
    } finally {
      setSaving(false)
    }
  }

  if (user && user.intakeRequired !== true) return <Navigate to="/dashboard" replace />

  const field = (label: string, key: string, type = 'text', extra?: any) => (
    <label className="block text-sm text-[#111827] mb-3">
      <span className="font-medium">{label}</span>
      <input
        type={type}
        value={form[key] ?? ''}
        disabled={extra?.disabled}
        onChange={(e) => set(key, e.target.value)}
        className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 bg-white disabled:bg-gray-100"
      />
    </label>
  )

  return (
    <div className="food-plan-root min-h-screen bg-[#f9fafb] text-[#111827] p-4">
      <div className="max-w-lg mx-auto bg-white border border-gray-100 rounded-3xl p-6 shadow-sm">
        <p className="text-xs font-semibold uppercase tracking-wide text-[#64ba30] mb-2">Food Plan</p>
        <h1 className="text-2xl font-bold mb-3">Bienvenid@</h1>
        <p className="text-sm text-gray-600 mb-5">
          Queremos que tengas una de las mejores experiencias en el inicio de tu proceso. Por eso necesitamos de tu ayuda para construir mejor tu perfil, que nos ayudará a crear el mejor plan para ti de acuerdo a tus objetivos.
        </p>
        {step === 0 && (
          <>
            {field('Correo electrónico', 'email', 'email', { disabled: true })}
            {field('Nombre y apellidos', 'fullName', 'text', { disabled: true })}
            {field('Teléfono', 'phone', 'tel')}
            {field('Edad', 'age', 'number')}
            {field('Fecha de cumpleaños', 'birthDate', 'date')}
            {field('Ciudad', 'city')}
            <p className="text-sm font-medium mb-2">Género</p>
            {['MALE', 'FEMALE', 'OTHER'].map((g) => (
              <label key={g} className="flex items-center gap-2 text-sm mb-1">
                <input type="radio" name="gender" checked={form.gender === g} onChange={() => set('gender', g)} />
                {g === 'MALE' ? 'Masculino' : g === 'FEMALE' ? 'Femenino' : 'Prefiero no decirlo'}
              </label>
            ))}
            {field('Profesión', 'profession')}
            {field('Estatura (cm)', 'heightCm', 'number')}
            {field('Peso (kg)', 'weightKg', 'number')}
            <p className="text-sm mb-3">IMC: <strong>{bmi || '—'}</strong></p>
            {field('% de grasa (si lo conoces)', 'bodyFatPct', 'number')}
          </>
        )}
        {step === 1 && (
          <>
            <p className="text-sm font-medium mb-2">Objetivo</p>
            {OBJECTIVES.map((o) => (
              <label key={o.id} className="flex items-start gap-2 text-sm mb-2">
                <input type="radio" name="objective" checked={form.objective === o.id} onChange={() => set('objective', o.id)} />
                <span>{o.label}</span>
              </label>
            ))}
            <label className="block text-sm mb-3">
              Descripción del objetivo
              <textarea value={form.objectiveDetail} onChange={(e) => set('objectiveDetail', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2" rows={3} />
            </label>
            {field('Días disponibles para entrenar', 'daysAvailable')}
            <p className="text-sm font-medium mb-2">¿Qué días quieres entrenar?</p>
            {DAYS.map((day) => (
              <label key={day} className="flex items-center gap-2 text-sm mb-1">
                <input type="checkbox" checked={form.trainDays.includes(day)} onChange={() => toggleDay(day)} />
                {day}
              </label>
            ))}
            {field('Lugar de entrenamiento (nombre del gimnasio)', 'gymName')}
            {field('Tiempo disponible para entrenar', 'trainMinutes')}
            {field('¿Cuánto tiempo lleva entrenando?', 'trainingExperience')}
            {field('¿Qué tipo de entrenamiento ha realizado durante los últimos meses?', 'trainingPreference')}
            <label className="block text-sm mb-3">
              ¿Alguna preferencia que desea incluir en los entrenamientos?
              <textarea value={form.trainingNotes || ''} onChange={(e) => set('trainingNotes', e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2" rows={2} />
            </label>
          </>
        )}
        {step === 2 && (
          <>
            {[
              ['injuries', 'Precedentes — ¿Lesiones previas? ¿Hace cuánto?'],
              ['diseases', '¿Enfermedades previas y/o actuales? ¿Toma algún medicamento?'],
              ['specialPathology', '¿El médico le ha manifestado una patología que demande necesidades especiales? ¿Qué directrices le dieron?'],
              ['dizziness', '¿Pierde el equilibrio mientras entrena a causa de mareos? ¿Con qué frecuencia?'],
              ['heartMeds', '¿Le han prescrito medicamentos para la presión arterial o el corazón? ¿Cuáles?'],
              ['otherLimits', '¿Alguna otra razón para consideraciones específicas en el entrenamiento?'],
              ['sport', '¿Actividad deportiva? ¿Con qué frecuencia?'],
              ['smokeAlcohol', '¿Fuma? ¿Alcohol? ¿Cuánto y desde cuándo?'],
              ['sleepHours', '¿Cuántas horas al día duermes? ¿Otros hábitos que interfieran con el rendimiento?'],
              ['workSchedule', '¿Cuál es su horario laboral/estudios? ¿Practica otro deporte? Frecuencia / horario'],
            ].map(([key, label]) => (
              <label key={key} className="block text-sm mb-3">
                {label}
                <textarea value={form[key] || ''} onChange={(e) => set(key, e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2" rows={2} />
              </label>
            ))}
          </>
        )}
        {step === 3 && (
          <>
            <p className="text-sm font-medium mb-2">¿Cuántas comidas haces en tu día?</p>
            <div className="flex gap-2 mb-3">
              {MEAL_OPTIONS.map((n) => (
                <button key={n} type="button" onClick={() => set('mealsNow', String(n))} className={`px-3 py-2 rounded-xl border ${form.mealsNow === String(n) ? 'bg-[#64ba30] text-white' : 'bg-white'}`}>{n}</button>
              ))}
            </div>
            <p className="text-sm font-medium mb-2">¿Cuántas comidas quieres comer en tu día?</p>
            <div className="flex gap-2 mb-3">
              {MEAL_OPTIONS.map((n) => (
                <button key={n} type="button" onClick={() => set('mealsWanted', String(n))} className={`px-3 py-2 rounded-xl border ${form.mealsWanted === String(n) ? 'bg-[#64ba30] text-white' : 'bg-white'}`}>{n}</button>
              ))}
            </div>
            {[
              ['eatOut', '¿Realiza alguna comida fuera de casa o en el trabajo?'],
              ['unwantedFoods', 'Alimentos no deseados'],
              ['waterLiters', '¿Cuántos litros de agua toma al día?'],
              ['supplements', 'Suplementación actual (nombre, marca, dosis)'],
              ['allergies', 'Intolerancias o alergias'],
              ['wantedFoods', 'Alimentos que deseas incluir'],
              ['socialMeals', '¿Suele tener compromisos sociales que interfieran con su alimentación?'],
            ].map(([key, label]) => (
              <label key={key} className="block text-sm mb-3">
                {label}
                <textarea value={form[key] || ''} onChange={(e) => set(key, e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2" rows={2} />
              </label>
            ))}
          </>
        )}
        {error ? <p className="text-sm text-red-600 mb-3">{error}</p> : null}
        <div className="flex gap-2 mt-4">
          {step > 0 ? <button type="button" className="flex-1 py-3 rounded-2xl border" onClick={() => setStep(step - 1)}>Atrás</button> : null}
          {step < 3 ? (
            <button type="button" className="flex-1 py-3 rounded-2xl bg-[#64ba30] text-white font-semibold" onClick={() => setStep(step + 1)}>Continuar</button>
          ) : (
            <button type="button" disabled={saving} className="flex-1 py-3 rounded-2xl bg-[#64ba30] text-white font-semibold" onClick={submit}>
              {saving ? 'Guardando…' : 'Enviar'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
